// Geometria do gráfico de colunas do consolidado.
// Fica fora do componente para poder ser testada — o lucro pode ser negativo,
// e barra invertida é justamente o caso que quebra em silêncio na tela.

import { numero } from './financeiroCalculos.js'

// Passo "redondo" mais próximo: 1, 2, 2,5 ou 5 vezes uma potência de dez.
// Sem isso o eixo sai com rótulos como 7,5k e 16,3k.
function passoRedondo(bruto){
    const magnitude = Math.pow(10, Math.floor(Math.log10(bruto)))
    const normalizado = bruto / magnitude

    if(normalizado <= 1){
        return magnitude
    }
    if(normalizado <= 2){
        return 2 * magnitude
    }
    if(normalizado <= 2.5){
        return 2.5 * magnitude
    }
    if(normalizado <= 5){
        return 5 * magnitude
    }
    return 10 * magnitude
}

// Escala com o zero sempre incluído e extremos em múltiplos do passo, para que
// toda linha de grade caia em um número redondo.
export function calcularEscala(valores, divisoesDesejadas){
    const lista = (valores || []).map(numero).filter((valor) => Number.isFinite(valor))
    const maximo = Math.max(0, ...lista)
    const minimo = Math.min(0, ...lista)
    const amplitude = maximo - minimo

    if(!amplitude){
        return { topo: 1, base: 0, passo: 1 }
    }

    const divisoes = divisoesDesejadas || 4
    const passo = passoRedondo(amplitude / divisoes)
    const topo = Math.ceil(maximo / passo) * passo
    const base = Math.floor(minimo / passo) * passo

    return { topo: topo === base ? topo + passo : topo, base, passo }
}

export function posicaoY(valor, escala, altura, margem){
    const alturaUtil = altura - margem.cima - margem.baixo
    const intervalo = (escala.topo - escala.base) || 1
    const proporcao = (numero(valor) - escala.base) / intervalo
    return margem.cima + alturaUtil - proporcao * alturaUtil
}

// Barra com as duas pontas do lado do valor arredondadas em 4px e a outra
// extremidade encostada na linha do zero. Vale para valor positivo e negativo.
export function caminhoBarra(x, largura, valor, escala, altura, margem){
    const yZero = posicaoY(0, escala, altura, margem)
    const yValor = posicaoY(valor, escala, altura, margem)
    const alturaBarra = Math.abs(yValor - yZero)

    if(alturaBarra < 0.5){
        // Valor zero: risco fino sobre a linha de base, para o mês não sumir.
        return `M ${x} ${yZero} h ${largura} v 1 h ${-largura} Z`
    }

    const raio = Math.min(4, largura / 2, alturaBarra)
    const sentido = numero(valor) >= 0 ? 1 : -1
    const yCurva = yValor + raio * sentido

    return [
        `M ${x} ${yZero}`,
        `V ${yCurva}`,
        `Q ${x} ${yValor} ${x + raio} ${yValor}`,
        `H ${x + largura - raio}`,
        `Q ${x + largura} ${yValor} ${x + largura} ${yCurva}`,
        `V ${yZero}`,
        'Z'
    ].join(' ')
}

export function montarGrupos(resumos, series, opcoes){
    const lista = resumos || []
    if(!lista.length){
        return []
    }

    const { largura, altura, margem, escala, rotulo } = opcoes
    const areaUtil = largura - margem.esquerda - margem.direita
    const larguraGrupo = areaUtil / lista.length
    const espacoBarras = Math.min(larguraGrupo * 0.72, 78)
    const vao = 2 // respiro de 2px entre barras vizinhas
    const larguraBarra = Math.max(
        (espacoBarras - vao * (series.length - 1)) / series.length,
        1
    )

    return lista.map((item, indice) => {
        const x = margem.esquerda + larguraGrupo * indice
        const inicio = x + (larguraGrupo - espacoBarras) / 2

        return {
            mes: item.mes,
            rotulo: rotulo ? rotulo(item.mes) : item.mes,
            x,
            largura: larguraGrupo,
            centro: x + larguraGrupo / 2,
            barras: series.map((serie, ordem) => ({
                serie: serie.chave,
                cor: serie.cor,
                valor: numero(item[serie.chave]),
                path: caminhoBarra(
                    inicio + (larguraBarra + vao) * ordem,
                    larguraBarra,
                    numero(item[serie.chave]),
                    escala,
                    altura,
                    margem
                )
            }))
        }
    })
}

// Uma linha por múltiplo do passo, do piso ao topo — assim os rótulos são
// sempre números redondos e o zero ganha linha própria.
export function montarLinhasGrade(escala, altura, margem){
    const linhas = []
    const total = Math.round((escala.topo - escala.base) / escala.passo)

    for(let i = 0; i <= total; i++){
        const valor = escala.base + escala.passo * i
        linhas.push({
            valor,
            y: posicaoY(valor, escala, altura, margem),
            zero: Math.abs(valor) < 0.0001
        })
    }

    return linhas
}
